import React from "react";
import { ArrowUp, ArrowDown, Network, Calendar, SplitSquareHorizontal } from "lucide-react";
import { ROUTES_CONFIG } from "src/constants/routesConfig";
import { PageLinkButton } from "src/components";
import { CollapsibleSection } from "src/components/ui/collapsible";

interface PurchaseOrderRelationshipsSectionProps {
  parentPurchaseOrderId?: string;
  childPurchaseOrderIds?: string[];
  backorderPurchaseOrderIds?: string[];
  originalPurchaseOrderId?: string;
  ukSubPurchaseOrderId?: string;
  euSubPurchaseOrderId?: string;
}

const PurchaseOrderRelationshipsSection: React.FC<PurchaseOrderRelationshipsSectionProps> = ({
  parentPurchaseOrderId,
  childPurchaseOrderIds,
  backorderPurchaseOrderIds,
  originalPurchaseOrderId,
  ukSubPurchaseOrderId,
  euSubPurchaseOrderId
}) => {
  if (
    !parentPurchaseOrderId &&
    (!childPurchaseOrderIds || childPurchaseOrderIds.length === 0) &&
    (!backorderPurchaseOrderIds || backorderPurchaseOrderIds.length === 0) &&
    !originalPurchaseOrderId &&
    !ukSubPurchaseOrderId &&
    !euSubPurchaseOrderId
  ) {
    return null;
  }

  return (
    <div className="bg-white border border-secondary-200 rounded-xl shadow-soft overflow-hidden">
      <CollapsibleSection
        initiallyOpen={false}
        title={
          <h3 className="text-lg font-semibold text-secondary-800 flex items-center">
            <Network className="w-5 h-5 mr-2 text-primary-600" />
            Purchase Order Relationships
          </h3>
        }
      >
        <div className="p-6 space-y-6">
          {/* Original Purchase Order (for backorder POs) */}
          {originalPurchaseOrderId && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Calendar className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-secondary-800 mb-2">Original Purchase Order</p>
                  <p className="text-xs text-amber-700 mb-2">
                    This is a backorder created from the original purchase order
                  </p>
                  <PageLinkButton
                    path={`${ROUTES_CONFIG.purchaseOrders.path}?id=${originalPurchaseOrderId}`}
                    text={originalPurchaseOrderId}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Parent Purchase Order */}
          {parentPurchaseOrderId && (
            <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <ArrowUp className="w-5 h-5 text-primary-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-secondary-800 mb-2">Parent Purchase Order</p>
                  <PageLinkButton
                    path={`${ROUTES_CONFIG.purchaseOrders.path}?id=${parentPurchaseOrderId}`}
                    text={parentPurchaseOrderId}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Child Purchase Orders */}
          {childPurchaseOrderIds && childPurchaseOrderIds.length > 0 && (
            <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <ArrowDown className="w-5 h-5 text-secondary-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-secondary-800 mb-3">Child Purchase Orders</p>
                  <div className="space-y-2">
                    {childPurchaseOrderIds.map((childId) => (
                      <div key={childId} className="flex items-center space-x-2 pl-4">
                        <span className="text-secondary-500">•</span>
                        <PageLinkButton path={`${ROUTES_CONFIG.purchaseOrders.path}?id=${childId}`} text={childId} />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Backorder Purchase Orders */}
          {backorderPurchaseOrderIds && backorderPurchaseOrderIds.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Calendar className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-secondary-800 mb-1">Backorder Purchase Orders</p>
                  <p className="text-xs text-amber-700 mb-3">Scheduled orders for out-of-stock items from this PO</p>
                  <div className="space-y-2">
                    {backorderPurchaseOrderIds.map((backorderId) => (
                      <div key={backorderId} className="flex items-center space-x-2 pl-4">
                        <span className="text-amber-500">•</span>
                        <PageLinkButton
                          path={`${ROUTES_CONFIG.purchaseOrders.path}?id=${backorderId}`}
                          text={backorderId}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Sub Purchase Orders (UK / EU) */}
          {(ukSubPurchaseOrderId || euSubPurchaseOrderId) && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <SplitSquareHorizontal className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-secondary-800 mb-3">Sub Purchase Orders</p>
                  <div className="space-y-2">
                    {ukSubPurchaseOrderId && (
                      <div className="flex items-center gap-3 pl-4">
                        <span className="text-emerald-500 text-xs font-semibold w-6 shrink-0">UK</span>
                        <span className="inline-block min-w-[11rem] max-w-full">
                          <PageLinkButton
                            path={`${ROUTES_CONFIG.subPurchaseOrder.path}?id=${ukSubPurchaseOrderId}`}
                            text={ukSubPurchaseOrderId}
                          />
                        </span>
                      </div>
                    )}
                    {euSubPurchaseOrderId && (
                      <div className="flex items-center gap-3 pl-4">
                        <span className="text-emerald-500 text-xs font-semibold w-6 shrink-0">EU</span>
                        <span className="inline-block min-w-[11rem] max-w-full">
                          <PageLinkButton
                            path={`${ROUTES_CONFIG.subPurchaseOrder.path}?id=${euSubPurchaseOrderId}`}
                            text={euSubPurchaseOrderId}
                          />
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </CollapsibleSection>
    </div>
  );
};

export { PurchaseOrderRelationshipsSection };
